import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import GlobalContext from '../../context/GlobalContext';
import { Spinner } from 'react-bootstrap';
import db from '../../Firebase';
import { DateTimeUtil } from '../../Utility';

const AgentCollection = "users";
// Set the strict cutoff date for API switching (Midnight IST)
const CUTOFF_DATE = new Date('2026-07-13T00:00:00+05:30'); 

const CallLogs = ({ customer_data }) => {
    const { permissible_roles } = useContext(GlobalContext);
    const [customerPhone, setCustomerPhone] = useState(null);
    const [unifiedLogs, setUnifiedLogs] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [agents, setAgents] = useState(new Map());

    useEffect(() => {
        if (customer_data) {
            setCustomerPhone(customer_data?.data?.customer_phone);
        }
    }, [customer_data]);

    useEffect(() => {
        if (customerPhone) {
            fetchUnifiedCallLogs(customerPhone);
        }
    }, [customerPhone]);

    // Fetch Agent Names based on the unique Agent numbers found in the combined logs
    useEffect(() => {
        if (unifiedLogs.length > 0) {
            const agentNumbers = [...new Set(unifiedLogs.map(log => log.agentPhone).filter(num => num !== "NA"))];
            if (agentNumbers.length > 0) {
                updateAgents(agentNumbers);
            }
        }
    }, [unifiedLogs]);

    const fetchAgentData = async (agentNumbers) => {
        try {
            const querySnapshot = await db.collection(AgentCollection)
                .where("phone_no", "in", agentNumbers)
                .get();

            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error("Agent data fetch error: ", error);
            return [];
        }
    };

    const updateAgents = async (agentNumbers) => {
        const agentsData = await fetchAgentData(agentNumbers);
        const newAgentsMap = new Map();
        agentsData.forEach(agent => {
            newAgentsMap.set(agent.phone_no, `${agent.first_name || ""} ${agent.last_name || ""}`);
        });
        setAgents(newAgentsMap);
    };

    const fetchUnifiedCallLogs = async (phone) => {
    setLoading(true);
    let knowlarityLogs = [];
    let exotelLogs = [];

    // 1. Fetch Knowlarity Logs (Directly)
    try {
        const knowlarityHeaders = {
            'Content-Type': 'application/json',
            'x-api-key': 'ft7e6lfTwA4rkgpRvMRMR79982o5hcOy5oe5MYp8',
            'Authorization': 'fd528b13-d2e2-48d0-80f8-2474ce9c6783'
        };
        const knowlarityRes = await axios.get(`https://kpi.knowlarity.com/Basic/v1/account/calllog?customer_number=%2B91${phone}`, { headers: knowlarityHeaders });
        
        // Normalize Knowlarity Data
        knowlarityLogs = (knowlarityRes.data.objects || []).map(log => {
            const callDate = new Date(log.start_time);
            return {
                source: "Knowlarity",
                agentPhone: (log.destination.match(/^\+91(\d{10})$/) || [])[1] || "NA",
                callType: log.Call_Type === 1 ? 'Outgoing' : 'Incoming',
                startTimeRaw: log.start_time,
                startTimeDate: callDate,
                duration: log.call_duration,
                recordingUrl: log.call_recording,
                timestampValid: callDate.getTime()
            };
        }).filter(log => log.startTimeDate < CUTOFF_DATE); // Older than July 13, 2026

    } catch (error) {
        console.error("Error fetching Knowlarity logs:", error);
    }

    // 2. Fetch Exotel Logs (Via Backend)
    try {
        const formattedPhone = phone.startsWith("+91") ? phone.slice(3) : phone;
        const exotelRes = await axios.get(`https://whytefarms.com/api/exotel-logs?customerPhone=${formattedPhone}`);
        
        if (exotelRes.data.success && exotelRes.data.data.Calls) {
            // Normalize Exotel Data based on actual JSON structure
            exotelLogs = exotelRes.data.data.Calls.map(log => {
                // Fix Exotel's date string for cross-browser compatibility and append IST offset
                const safeDateString = log.StartTime.replace(" ", "T") + "+05:30"; 
                const callDate = new Date(safeDateString);
                
                // Exotel adds a leading 0 to agent numbers (e.g., 09873886656). Strip it to 10 digits.
                const rawAgentPhone = log.From || "";
                const cleanAgentPhone = rawAgentPhone.startsWith("0") && rawAgentPhone.length === 11 
                    ? rawAgentPhone.slice(1) 
                    : rawAgentPhone;

                return {
                    source: "Exotel",
                    agentPhone: cleanAgentPhone || "NA", 
                    callType: log.Direction.includes('outbound') ? 'Outgoing' : 'Incoming',
                    startTimeRaw: log.StartTime, 
                    startTimeDate: callDate,
                    duration: log.Duration,
                    recordingUrl: log.RecordingUrl, // Will be "" if empty, which is handled in UI
                    timestampValid: callDate.getTime()
                };
            }).filter(log => log.startTimeDate >= CUTOFF_DATE); // On or after July 13, 2026
        }
    } catch (error) {
        console.error("Error fetching Exotel logs via backend:", error);
    }

    // 3. Merge and Sort
    const combinedLogs = [...knowlarityLogs, ...exotelLogs];
    
    // Sort by Date (Newest first)
    combinedLogs.sort((a, b) => b.timestampValid - a.timestampValid);

    setUnifiedLogs(combinedLogs);
    setLoading(false);
};

    if (!customerPhone) {
        return <div>Customer does not have a phone number</div>;
    }

    return (
        <>
            {loading ? (
                <Spinner animation="border" role="status" />
            ) : (
                <div className="container mt-4">
                    {unifiedLogs.length === 0 && <p>No call logs found.</p>}
                    
                    {unifiedLogs.map((callLog, index) => (
                        <div key={index} className="card mb-3" style={{ width: '100%' }}>
                            <div className="card-body">
                                <div className="row mb-2">
                                    <div className="col">
                                        <p className="card-text">
                                            <strong>Call Type:</strong> {callLog.callType} 
                                            <span className="badge bg-primary ms-2">{callLog.source}</span>
                                        </p>
                                    </div>
                                    <div className="col">
                                        <p className="card-text"><strong>Agent Phone:</strong> {callLog.agentPhone}</p>
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col">
                                        <p className="card-text">
                                            <strong>Call Date:</strong> {
                                                callLog.source === "Knowlarity" 
                                                ? `${DateTimeUtil.timestampToDate(callLog.startTimeRaw)} ${DateTimeUtil.timestampToTimeAMPM(callLog.startTimeRaw)}`
                                                : callLog.startTimeDate.toLocaleString() // Fallback formatting for Exotel dates
                                            }
                                        </p>
                                    </div>
                                    <div className="col">
                                        <p className="card-text"><strong>Agent Name:</strong> {agents.get(callLog.agentPhone) || "Unknown"}</p>
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col">
                                        <p className="card-text"><strong>Call Duration:</strong> {callLog.duration} seconds</p>
                                    </div>
                                </div>
                                {callLog.recordingUrl && (
                                <div className="d-flex justify-content-between align-items-center">
                                    <audio controls preload="none">
                                        <source 
                                            src={
                                                callLog.source === "Knowlarity" 
                                                ? callLog.recordingUrl 
                                                : `https://whytefarms.com/api/play-exotel-audio?url=${encodeURIComponent(callLog.recordingUrl)}`
                                            } 
                                            type={callLog.source === "Knowlarity" ? "audio/wav" : "audio/mpeg"} 
                                        />
                                        Your browser does not support the audio element.
                                    </audio>
                                </div>
                            )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export default CallLogs;